import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { format } from 'date-fns'
import {
  ArrowLeft, User, Phone, Mail, MapPin, GraduationCap, Wallet, Coins,
  Gift, CheckCircle2, XCircle, Ban, Clock, FileText, Lock, ShieldCheck,
  CalendarPlus, AlertTriangle, X, Receipt, Percent, Timer, DollarSign,
} from 'lucide-react'
import { getEffectiveRate, formatRate, hasRateOverride } from '../lib/rates'
import { formatMoney, formatPercent, outOfPocket } from '../lib/pricing'
import { setCreditFollowup } from '../lib/consulting'
import { createNotification, logActivity, resolveNotificationsFor } from '../lib/activity'
import { ActivityFeed } from '../components/ActivityFeed'
import { fetchMyTutorIds, hasAdminRights } from '../lib/roles'

type StudentRow = {
  id: string; name: string; phone: string | null; address: string | null; grade: string | null
  tutor_id: string | null; guardian_name: string | null; guardian_email: string | null
  guardian_phone: string | null; guardian_address: string | null; balance_minutes: number
  hourly_rate_override: number | null; price_per_session: number | null
  insurance_coverage_percent: number | null; session_length_minutes: number | null
  status: 'active' | 'inactive'; notes: string | null; created_at?: string
}
type SessionRow = {
  id: string; student_id: string; tutor_id: string | null; started_at: string; ended_at: string
  minutes: number; notes: string | null; approval_status: 'pending' | 'approved' | 'rejected'
  session_type: 'tutoring' | 'consulting'; lifecycle_status: 'completed' | 'cancelled'
  is_free: boolean; cancel_reason: string | null; cancelled_at: string | null
}
type CreditRow = {
  id: string; earned_at: string; earned_at_session_count: number | null
  status: 'available' | 'claimed' | 'revoked'; followup_status: 'accepted' | 'declined' | null
  followup_at: string | null; followup_by: string | null; delivered_session_id: string | null
  revoked_reason: string | null
}
type RateRequestRow = {
  id: string; current_rate: number | null; requested_rate: number; status: 'pending' | 'approved' | 'rejected'
  requested_by: string | null; created_at: string
}

function minutesToHuman(min: number): string {
  const sign = min < 0 ? '-' : ''
  const m = Math.abs(min)
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${sign}${r}m`
  return `${sign}${h}h ${r}m`
}

export function StudentDetail() {
  const { id } = useParams<{ id: string }>()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const isAdmin = hasAdminRights(profile?.role)
  const actor = profile ? { id: profile.id, email: profile.email, role: profile.role } : null

  const [student, setStudent] = useState<StudentRow | null>(null)
  const [tutorName, setTutorName] = useState<string>('Unassigned')
  const [tutorGeneralRate, setTutorGeneralRate] = useState<number | null>(null)
  const [tutorNames, setTutorNames] = useState<Record<string, string>>({})
  const [tutorOptions, setTutorOptions] = useState<{ id: string; name: string }[]>([])
  const [editingTutor, setEditingTutor] = useState(false)
  const [tutorChoice, setTutorChoice] = useState('')
  const [tutorBusy, setTutorBusy] = useState(false)
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [credits, setCredits] = useState<CreditRow[]>([])
  const [pendingRate, setPendingRate] = useState<RateRequestRow | null>(null)
  const [myTutorIds, setMyTutorIds] = useState<string[]>([])
  const myTutorId = myTutorIds.find(t => t === student?.tutor_id) ?? myTutorIds[0] ?? null
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)

  const [tab, setTab] = useState<'done' | 'cancelled'>('done')
  const [rateInput, setRateInput] = useState('')
  const [showRateForm, setShowRateForm] = useState(false)
  const [rateBusy, setRateBusy] = useState(false)
  const [viewingNotes, setViewingNotes] = useState<SessionRow | null>(null)
  const [claimCredit, setClaimCredit] = useState<CreditRow | null>(null)
  const [showBillingForm, setShowBillingForm] = useState(false)
  const [billingBusy, setBillingBusy] = useState(false)
  const [billingForm, setBillingForm] = useState({ price: '', insurance: '', length: '60' })

  const ownsStudent = isAdmin || (!!student?.tutor_id && myTutorIds.includes(student.tutor_id))

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const admin = hasAdminRights(profile?.role)
      // Resolve current tutor's own id (for ownership + rate requests).
      let resolvedTutorIds: string[] = []
      if (profile?.role === 'tutor') {
        resolvedTutorIds = await fetchMyTutorIds()
        setMyTutorIds(resolvedTutorIds)
      }

      const { data: s, error: sErr } = await supabase.from('students').select('*').eq('id', id).single()
      if (sErr || !s) { setForbidden(true); setLoading(false); return }

      if (profile?.role === 'tutor' && !resolvedTutorIds.includes(s.tutor_id)) {
        const { data: teaches, error: teachesError } = await supabase.rpc('tutor_sees_student', { p_student_id: id })
        if (teachesError || !teaches) { setForbidden(true); setLoading(false); return }
      }

      // Money (price/insurance/rate override) lives in the admin-only student_billing table.
      let studentRow = { ...s, price_per_session: null, insurance_coverage_percent: null, hourly_rate_override: null } as StudentRow
      if (admin) {
        const { data: sb } = await supabase.from('student_billing')
          .select('price_per_session, insurance_coverage_percent, hourly_rate_override')
          .eq('student_id', id).maybeSingle()
        studentRow = {
          ...studentRow,
          price_per_session: sb?.price_per_session ?? null,
          insurance_coverage_percent: sb?.insurance_coverage_percent ?? null,
          hourly_rate_override: sb?.hourly_rate_override ?? null,
        }
      }
      setStudent(studentRow)

      const [{ data: allTutors }, { data: sess }, { data: cred }, { data: rate }] = await Promise.all([
        supabase.from('tutors').select('id, name, active').order('name'),
        supabase.from('sessions').select('*').eq('student_id', id).order('started_at', { ascending: false }),
        supabase.from('consulting_credits').select('*').eq('student_id', id).order('earned_at', { ascending: false }),
        supabase.from('rate_change_requests').select('*').eq('student_id', id).eq('status', 'pending').order('created_at', { ascending: false }).limit(1),
      ])

      const nameMap: Record<string, string> = {}
      ;(allTutors || []).forEach((t: any) => { nameMap[t.id] = t.name })
      setTutorNames(nameMap)
      setTutorOptions((allTutors || [])
        .filter((t: any) => t.active !== false || t.id === s.tutor_id)
        .map((t: any) => ({ id: t.id, name: t.name })))
      if (s.tutor_id) {
        setTutorName(nameMap[s.tutor_id] || 'Unassigned')
        // Tutor's general hourly rate is money — from the admin-only tutor_billing table.
        if (admin) {
          const { data: tb } = await supabase.from('tutor_billing').select('hourly_rate').eq('tutor_id', s.tutor_id).maybeSingle()
          setTutorGeneralRate(tb?.hourly_rate ?? null)
        }
      } else {
        setTutorName('Unassigned')
        setTutorGeneralRate(null)
      }
      setSessions((sess as SessionRow[]) || [])
      setCredits((cred as CreditRow[]) || [])
      setPendingRate(((rate as RateRequestRow[]) || [])[0] || null)
    } catch (e) {
      console.error('Error loading student detail:', e)
      setForbidden(true)
    } finally {
      setLoading(false)
    }
  }, [id, profile])

  useEffect(() => { load() }, [load])

  const effectiveRate = useMemo(
    () => getEffectiveRate(student?.hourly_rate_override, tutorGeneralRate),
    [student, tutorGeneralRate]
  )

  const doneSessions = sessions.filter((s) => s.lifecycle_status !== 'cancelled')
  const cancelledSessions = sessions.filter((s) => s.lifecycle_status === 'cancelled')
  const availableCredits = credits.filter((c) => c.status === 'available')

  // ---- Rate handlers --------------------------------------------------
  const submitTutorRequest = async () => {
    const val = parseFloat(rateInput)
    if (isNaN(val) || val < 0) { alert('Enter a valid rate.'); return }
    if (!student || !myTutorId) return
    setRateBusy(true)
    try {
      const { error } = await supabase.from('rate_change_requests').insert({
        student_id: student.id, tutor_id: myTutorId,
        current_rate: effectiveRate, requested_rate: val,
        status: 'pending', requested_by: profile?.email,
      })
      if (error) {
        if ((error as any).code === '23505') { alert('A rate change is already pending for this student.'); }
        else throw error
      } else {
        await createNotification({
          type: 'rate_request',
          title: `Rate change requested for ${student.name}`,
          message: `${tutorName} requested ${formatRate(effectiveRate)} → ${formatRate(val)}.`,
          relatedTable: 'students', relatedId: student.id,
          route: `/dashboard/students/${student.id}`, severity: 'warning',
        })
        await logActivity({
          scope: 'tutors', action: 'rate_requested',
          summary: `${tutorName} requested rate change for ${student.name}: ${formatRate(effectiveRate)} → ${formatRate(val)}`,
          entityTable: 'students', entityId: student.id, metadata: { studentId: student.id }, actor,
        })
      }
      setShowRateForm(false); setRateInput('')
      await load()
    } catch (e) {
      console.error('Rate request failed:', e); alert('Could not submit rate request.')
    } finally { setRateBusy(false) }
  }

  const adminSetRate = async () => {
    const val = parseFloat(rateInput)
    if (isNaN(val) || val < 0) { alert('Enter a valid rate.'); return }
    if (!student) return
    setRateBusy(true)
    try {
      // Rate override is money — admin-only student_billing table.
      const { error } = await supabase.from('student_billing').upsert({
        student_id: student.id, hourly_rate_override: val, updated_at: new Date().toISOString(),
      })
      if (error) throw error
      await logActivity({
        scope: 'students', action: 'rate_set',
        summary: `Admin set ${student.name}'s rate to ${formatRate(val)}`,
        entityTable: 'students', entityId: student.id, metadata: { studentId: student.id }, actor,
      })
      setShowRateForm(false); setRateInput('')
      await load()
    } catch (e) {
      console.error('Set rate failed:', e); alert('Could not set rate.')
    } finally { setRateBusy(false) }
  }

  // ---- Billing & sessions handlers -----------------------------------
  const openBillingForm = () => {
    if (!student) return
    setBillingForm({
      price: student.price_per_session != null ? String(student.price_per_session) : '',
      insurance: student.insurance_coverage_percent != null ? String(student.insurance_coverage_percent) : '',
      length: student.session_length_minutes != null ? String(student.session_length_minutes) : '60',
    })
    setShowBillingForm(true)
  }

  const saveTutor = async () => {
    if (!student) return
    const next = tutorChoice || null
    if (next === student.tutor_id) { setEditingTutor(false); return }
    setTutorBusy(true)
    try {
      const { error } = await supabase.from('students').update({ tutor_id: next }).eq('id', student.id)
      if (error) throw error
      await logActivity({
        scope: 'students', action: 'tutor_changed',
        summary: `Changed ${student.name}'s tutor: ${tutorName} → ${next ? tutorNames[next] || 'Unknown tutor' : 'Unassigned'}`,
        entityTable: 'students', entityId: student.id, metadata: { studentId: student.id, from: student.tutor_id, to: next }, actor,
      })
      setEditingTutor(false)
      await load()
    } catch (e: any) {
      console.error('Change tutor failed:', e)
      alert(e?.message ? `Could not change the tutor: ${e.message}` : 'Could not change the tutor.')
    } finally { setTutorBusy(false) }
  }

  const saveBilling = async () => {
    if (!student) return
    const price = billingForm.price === '' ? null : parseFloat(billingForm.price)
    const insurance = billingForm.insurance === '' ? null : parseFloat(billingForm.insurance)
    const length = billingForm.length === '' ? 60 : parseInt(billingForm.length)
    if (price !== null && (isNaN(price) || price < 0)) { alert('Enter a valid price per session.'); return }
    if (insurance !== null && (isNaN(insurance) || insurance < 0 || insurance > 100)) { alert('Insurance % must be between 0 and 100.'); return }
    if (isNaN(length) || length < 1) { alert('Session length must be at least 1 minute.'); return }
    setBillingBusy(true)
    try {
      // Session length is operational (stays on students); price + insurance are money (admin-only table).
      const { error: lenErr } = await supabase.from('students').update({ session_length_minutes: length }).eq('id', student.id)
      if (lenErr) throw lenErr
      const { error } = await supabase.from('student_billing').upsert({
        student_id: student.id,
        price_per_session: price,
        insurance_coverage_percent: insurance,
        updated_at: new Date().toISOString(),
      })
      if (error) throw error
      await logActivity({
        scope: 'students', action: 'billing_set',
        summary: `Updated billing for ${student.name}: ${formatMoney(price)}/session · ${formatPercent(insurance)} insurance · ${length} min`,
        entityTable: 'students', entityId: student.id, metadata: { studentId: student.id }, actor,
      })
      setShowBillingForm(false)
      await load()
    } catch (e) {
      console.error('Save billing failed:', e); alert('Could not save billing settings.')
    } finally { setBillingBusy(false) }
  }

  const decideRate = async (approve: boolean) => {
    if (!pendingRate || !student) return
    setRateBusy(true)
    try {
      if (approve) {
        // Rate override is money — admin-only student_billing table.
        const { error: upErr } = await supabase.from('student_billing')
          .upsert({ student_id: student.id, hourly_rate_override: pendingRate.requested_rate, updated_at: new Date().toISOString() })
        if (upErr) throw upErr
      }
      const { error: rqErr } = await supabase.from('rate_change_requests')
        .update({ status: approve ? 'approved' : 'rejected', decided_by: profile?.email, decided_at: new Date().toISOString() })
        .eq('id', pendingRate.id)
      if (rqErr) throw rqErr

      await resolveNotificationsFor('students', student.id, profile?.email, 'rate_request')
      await logActivity({
        scope: 'tutors', action: approve ? 'rate_approved' : 'rate_rejected',
        summary: `${approve ? 'Approved' : 'Rejected'} rate change for ${student.name}` +
          (approve ? ` (now ${formatRate(pendingRate.requested_rate)})` : ''),
        entityTable: 'students', entityId: student.id, metadata: { studentId: student.id }, actor,
      })
      await load()
    } catch (e) {
      console.error('Decide rate failed:', e); alert('Could not update the rate request.')
    } finally { setRateBusy(false) }
  }

  // ---- Consulting handlers -------------------------------------------
  const decideCredit = async (credit: CreditRow, decision: 'accepted' | 'declined') => {
    if (!student) return
    try {
      await setCreditFollowup(credit.id, decision, { actor, studentId: student.id, studentName: student.name })
      await load()
    } catch (e) {
      console.error('Credit decision failed:', e); alert('Could not record the decision.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400" />
      </div>
    )
  }

  if (forbidden || !student) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 dark:bg-rose-900/20">
          <Lock className="h-7 w-7 text-rose-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Student not available</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          This student doesn't exist or isn't assigned to you.
        </p>
        <button onClick={() => navigate('/dashboard/students')}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          <ArrowLeft className="h-4 w-4" /> Back to students
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto pb-10">
      {/* Back link */}
      <Link to="/dashboard/students"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 mb-4">
        <ArrowLeft className="h-4 w-4" /> Students
      </Link>

      {/* Identity header — asymmetric: identity left, key facts right */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 overflow-hidden">
        <div className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-white text-2xl font-bold">
              {student.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{student.name}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  student.status === 'active'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}>{student.status}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                {student.grade && <span className="inline-flex items-center gap-1"><GraduationCap className="h-4 w-4" /> Grade {student.grade}</span>}
                {editingTutor ? (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <User className="h-4 w-4" />
                    <select
                      value={tutorChoice}
                      onChange={(e) => setTutorChoice(e.target.value)}
                      disabled={tutorBusy}
                      aria-label="Tutor"
                      className="h-8 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-0 px-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-400"
                    >
                      <option value="">No tutor yet</option>
                      {tutorOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button disabled={tutorBusy} onClick={saveTutor}
                      className="h-8 px-3 rounded-lg bg-brand-600 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50">
                      {tutorBusy ? 'Saving…' : 'Save'}
                    </button>
                    <button disabled={tutorBusy} onClick={() => setEditingTutor(false)}
                      className="h-8 px-2 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0">
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <User className="h-4 w-4" /> {tutorName}
                    {isAdmin && (
                      <button onClick={() => { setTutorChoice(student.tutor_id ?? ''); setEditingTutor(true) }}
                        className="ml-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline">
                        Change
                      </button>
                    )}
                  </span>
                )}
                {student.created_at && <span className="inline-flex items-center gap-1"><CalendarPlus className="h-4 w-4" /> Added {format(new Date(student.created_at), 'MMM d, yyyy')}</span>}
              </div>
            </div>
          </div>

          {/* Key facts */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4">
            <Fact icon={Wallet} label="Balance" value={minutesToHuman(student.balance_minutes)} sub={`${student.balance_minutes} min`}
              tone={student.balance_minutes <= 0 ? 'danger' : 'default'} />
            {isAdmin && (
              <Fact icon={Coins} label="Rate" value={formatRate(effectiveRate)}
                sub={hasRateOverride(student.hourly_rate_override) ? 'custom' : 'general'} />
            )}
            <Fact icon={Gift} label="Consulting" value={`${availableCredits.length}`} sub="available" tone={availableCredits.length > 0 ? 'amber' : 'default'} />
          </div>
        </div>

        {/* Contact strip */}
        <div className="border-t border-gray-100 dark:border-gray-800 px-6 sm:px-8 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <ContactItem icon={Phone} label="Student phone" value={student.phone} />
          <ContactItem icon={User} label="Guardian" value={student.guardian_name} />
          <ContactItem icon={Mail} label="Guardian email" value={student.guardian_email} />
          <ContactItem icon={Phone} label="Guardian phone" value={student.guardian_phone} />
          {student.address && <ContactItem icon={MapPin} label="Address" value={student.address} />}
          {student.guardian_address && <ContactItem icon={MapPin} label="Guardian address" value={student.guardian_address} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Left / main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Rate management — money, admins only */}
          {isAdmin && (
          <Section title="Hourly rate" icon={Coins}>
            {pendingRate ? (
              <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      Rate change pending approval
                    </p>
                    <p className="text-sm text-amber-800 dark:text-amber-300 mt-0.5">
                      {formatRate(pendingRate.current_rate)} → <strong>{formatRate(pendingRate.requested_rate)}</strong>
                      {pendingRate.requested_by ? ` · requested by ${pendingRate.requested_by}` : ''}
                    </p>
                    <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1">
                      New sessions for this student are paused until this is decided.
                    </p>
                    {isAdmin && (
                      <div className="mt-3 flex gap-2">
                        <button disabled={rateBusy} onClick={() => decideRate(true)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                          <ShieldCheck className="h-4 w-4" /> Approve
                        </button>
                        <button disabled={rateBusy} onClick={() => decideRate(false)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white/60 dark:hover:bg-surface-0 disabled:opacity-50">
                          <XCircle className="h-4 w-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Current effective rate</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white">
                    {formatRate(effectiveRate)}{' '}
                    <span className="text-xs font-normal text-gray-400">
                      {hasRateOverride(student.hourly_rate_override) ? '(custom for this student)' : `(${tutorName}'s general rate)`}
                    </span>
                  </p>
                </div>
                {ownsStudent && !showRateForm && (
                  <button
                    onClick={() => { setRateInput(String(effectiveRate || '')); setShowRateForm(true) }}
                    className="self-start inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-0">
                    <Coins className="h-4 w-4" /> {isAdmin ? 'Set rate' : 'Request rate change'}
                  </button>
                )}
              </div>
            )}

            {showRateForm && !pendingRate && (
              <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-surface-0">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isAdmin ? 'New hourly rate' : 'Requested hourly rate'} (USD)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">$</span>
                  <input type="number" min="0" step="0.5" value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-100 text-gray-900 dark:text-white"
                    autoFocus />
                  <span className="text-sm text-gray-400">/hr</span>
                </div>
                {!isAdmin && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    This will be sent to an admin for approval. Logging sessions for {student.name} is paused until they decide.
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  <button disabled={rateBusy} onClick={isAdmin ? adminSetRate : submitTutorRequest}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
                    {rateBusy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    {isAdmin ? 'Save rate' : 'Send request'}
                  </button>
                  <button disabled={rateBusy} onClick={() => { setShowRateForm(false); setRateInput('') }}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-surface-100">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Section>
          )}

          {/* Billing & sessions — money, admins only */}
          {isAdmin && (
          <Section title="Billing & sessions" icon={Receipt}
            hint="Per-student defaults — what the family is charged and how long one session runs.">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                <Fact icon={DollarSign} label="Price / session" value={formatMoney(student.price_per_session)}
                  sub={student.price_per_session == null ? 'not set' : 'charged to family'} />
                <Fact icon={Percent} label="Insurance covers" value={formatPercent(student.insurance_coverage_percent)}
                  sub={student.insurance_coverage_percent == null ? 'not set' : undefined} />
                <Fact icon={Timer} label="Session length" value={`${student.session_length_minutes ?? 60} min`} sub="per session" />
              </div>
              {isAdmin && !showBillingForm && (
                <button onClick={openBillingForm}
                  className="self-start inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-0">
                  <Receipt className="h-4 w-4" /> Edit
                </button>
              )}
            </div>

            {student.price_per_session != null && student.insurance_coverage_percent != null && student.insurance_coverage_percent > 0 && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Insurance covers <strong className="text-gray-700 dark:text-gray-200">{formatMoney(student.price_per_session - (outOfPocket(student.price_per_session, student.insurance_coverage_percent) ?? 0))}</strong>{' '}
                per session · family pays <strong className="text-gray-700 dark:text-gray-200">{formatMoney(outOfPocket(student.price_per_session, student.insurance_coverage_percent))}</strong> out of pocket.
              </p>
            )}

            {showBillingForm && (
              <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-surface-0">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Price / session ($)</span>
                    <input type="number" min="0" step="0.01" value={billingForm.price} autoFocus
                      onChange={(e) => setBillingForm({ ...billingForm, price: e.target.value })}
                      placeholder="e.g. 60"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-100 text-gray-900 dark:text-white" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Insurance (%)</span>
                    <input type="number" min="0" max="100" step="1" value={billingForm.insurance}
                      onChange={(e) => setBillingForm({ ...billingForm, insurance: e.target.value })}
                      placeholder="e.g. 50"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-100 text-gray-900 dark:text-white" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Session length (min)</span>
                    <input type="number" min="1" step="1" value={billingForm.length}
                      onChange={(e) => setBillingForm({ ...billingForm, length: e.target.value })}
                      placeholder="60"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-100 text-gray-900 dark:text-white" />
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Leave price or insurance blank to clear them. These drive the auto-fill on the Top-ups page.
                </p>
                <div className="mt-3 flex gap-2">
                  <button disabled={billingBusy} onClick={saveBilling}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
                    {billingBusy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    Save
                  </button>
                  <button disabled={billingBusy} onClick={() => setShowBillingForm(false)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-surface-100">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Section>
          )}

          {/* Consulting credits */}
          <Section title="Consulting sessions" icon={Gift}
            hint="Earned 1 free session for every 2 approved tutoring sessions.">
            {credits.length === 0 ? (
              <EmptyHint icon={Gift} text="No consulting sessions earned yet." sub="They'll appear here automatically as approved sessions add up." />
            ) : (
              <ul className="space-y-2">
                {credits.map((c) => (
                  <li key={c.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                          c.status === 'claimed' ? 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30'
                          : c.status === 'revoked' ? 'bg-gray-100 text-gray-400 dark:bg-gray-800'
                          : 'bg-amber-50 text-amber-500 dark:bg-amber-900/30'}`}>
                          <Gift className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Free consulting session
                            <CreditBadge credit={c} />
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Earned {format(new Date(c.earned_at), 'MMM d, yyyy')}
                            {c.followup_status ? ` · ${c.followup_status} by ${c.followup_by ?? 'admin'}` : ''}
                            {c.status === 'revoked' && c.revoked_reason ? ` · ${c.revoked_reason}` : ''}
                          </p>
                        </div>
                      </div>

                      {c.status === 'available' && (
                        <div className="flex flex-wrap gap-2">
                          {isAdmin && c.followup_status !== 'accepted' && (
                            <button onClick={() => decideCredit(c, 'accepted')}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Accept
                            </button>
                          )}
                          {isAdmin && c.followup_status !== 'declined' && (
                            <button onClick={() => decideCredit(c, 'declined')}
                              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-0">
                              <XCircle className="h-3.5 w-3.5" /> Decline
                            </button>
                          )}
                          {ownsStudent && (
                            <button onClick={() => setClaimCredit(c)}
                              className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-700">
                              <CalendarPlus className="h-3.5 w-3.5" /> Log session
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Session history with Done / Cancelled tabs */}
          <Section title="Session history" icon={Clock}>
            <div className="flex gap-1 mb-4 rounded-lg bg-gray-100 dark:bg-surface-0 p-1 w-fit">
              <TabButton active={tab === 'done'} onClick={() => setTab('done')} label="Done" count={doneSessions.length} />
              <TabButton active={tab === 'cancelled'} onClick={() => setTab('cancelled')} label="Cancelled" count={cancelledSessions.length} />
            </div>

            {(tab === 'done' ? doneSessions : cancelledSessions).length === 0 ? (
              <EmptyHint icon={Clock} text={tab === 'done' ? 'No sessions yet.' : 'No cancelled sessions.'} />
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {(tab === 'done' ? doneSessions : cancelledSessions).map((s) => (
                  <li key={s.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {format(new Date(s.started_at), 'MMM d, yyyy')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {format(new Date(s.started_at), 'h:mm a')}–{format(new Date(s.ended_at), 'h:mm a')} · {s.minutes} min
                        </span>
                        {s.session_type === 'consulting' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            <Gift className="h-3 w-3" /> Free consulting
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {tutorNames[s.tutor_id || ''] || 'Unknown tutor'}
                        {tab === 'cancelled' && s.cancel_reason ? ` · ${s.cancel_reason}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {s.notes && (
                        <button onClick={() => setViewingNotes(s)}
                          className="inline-flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                          <FileText className="h-3.5 w-3.5" /> Notes
                        </button>
                      )}
                      <SessionStatusBadge session={s} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Right column: activity */}
        <div className="space-y-6">
          <Section title="Activity" icon={Clock}>
            <ActivityFeed studentId={student.id} emptyHint="No activity for this student yet." />
          </Section>
        </div>
      </div>

      {/* Notes modal */}
      {viewingNotes && (
        <Modal onClose={() => setViewingNotes(null)} title="Session notes"
          subtitle={`${student.name} • ${format(new Date(viewingNotes.started_at), 'MMM d, yyyy')}`}>
          <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {viewingNotes.notes}
          </pre>
        </Modal>
      )}

      {/* Claim consulting session modal */}
      {claimCredit && student && (
        <ClaimConsultingModal
          student={student}
          credit={claimCredit}
          actor={actor}
          onClose={() => setClaimCredit(null)}
          onDone={async () => { setClaimCredit(null); await load() }}
        />
      )}
    </div>
  )
}

// ---- Small presentational helpers -------------------------------------

function Fact({ icon: Icon, label, value, sub, tone = 'default' }: {
  icon: typeof Wallet; label: string; value: string; sub?: string; tone?: 'default' | 'amber' | 'danger'
}) {
  const valueTone = tone === 'danger' ? 'text-rose-600 dark:text-rose-400'
    : tone === 'amber' ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-900 dark:text-white'
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-surface-0 px-3 py-2.5 min-w-[6.5rem]">
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className={`mt-1 text-lg font-bold leading-none ${valueTone}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-gray-400">{sub}</p>}
    </div>
  )
}

function ContactItem({ icon: Icon, label, value }: { icon: typeof Phone; label: string; value: string | null }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <span className="text-gray-400 dark:text-gray-500">{label}:</span>
      <span className="text-gray-700 dark:text-gray-200 truncate">{value || '—'}</span>
    </div>
  )
}

function Section({ title, icon: Icon, hint, children }: {
  title: string; icon: typeof Coins; hint?: string; children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
          <Icon className="h-4 w-4 text-brand-500" /> {title}
        </h2>
        {hint && <p className="text-xs text-gray-400 mt-0.5 ml-6">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
      {label} <span className={`ml-1 text-xs ${active ? 'text-brand-500' : 'text-gray-400'}`}>{count}</span>
    </button>
  )
}

function EmptyHint({ icon: Icon, text, sub }: { icon: typeof Clock; text: string; sub?: string }) {
  return (
    <div className="py-8 text-center">
      <Icon className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
      {sub && <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">{sub}</p>}
    </div>
  )
}

function CreditBadge({ credit }: { credit: CreditRow }) {
  const cls = 'ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium align-middle'
  if (credit.status === 'claimed') return <span className={`${cls} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>Claimed</span>
  if (credit.status === 'revoked') return <span className={`${cls} bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400`}>Revoked</span>
  if (credit.followup_status === 'accepted') return <span className={`${cls} bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300`}>Accepted</span>
  if (credit.followup_status === 'declined') return <span className={`${cls} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Declined · reclaimable</span>
  return <span className={`${cls} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Available</span>
}

function SessionStatusBadge({ session }: { session: SessionRow }) {
  const cls = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium'
  if (session.lifecycle_status === 'cancelled')
    return <span className={`${cls} bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}><Ban className="h-3 w-3" /> Cancelled</span>
  if (session.approval_status === 'approved')
    return <span className={`${cls} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>Approved</span>
  if (session.approval_status === 'rejected')
    return <span className={`${cls} bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300`}>Rejected</span>
  return <span className={`${cls} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Pending</span>
}

function Modal({ title, subtitle, onClose, children }: {
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white dark:bg-surface-100 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-modalIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  )
}

// ---- Claim consulting session modal -----------------------------------

function ClaimConsultingModal({ student, credit, actor, onClose, onDone }: {
  student: StudentRow; credit: CreditRow; actor: any; onClose: () => void; onDone: () => void
}) {
  const [startedAt, setStartedAt] = useState('')
  const [endedAt, setEndedAt] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    if (!startedAt || !endedAt) { setErr('Please set start and end times.'); return }
    const start = new Date(startedAt), end = new Date(endedAt)
    if (end <= start) { setErr('End time must be after start time.'); return }
    if (!student.tutor_id) { setErr('This student has no assigned tutor.'); return }
    setErr(null); setBusy(true)
    try {
      const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
      // Free consulting session: tagged, auto-approved (no balance deduction), linked to the credit.
      const { data: sess, error: sErr } = await supabase.from('sessions').insert({
        student_id: student.id, tutor_id: student.tutor_id,
        started_at: startedAt, ended_at: endedAt, minutes, notes: notes || null,
        approval_status: 'approved', approved_by: actor?.email ?? null, approved_at: new Date().toISOString(),
        session_type: 'consulting', is_free: true, lifecycle_status: 'completed',
        consulting_credit_id: credit.id,
      }).select().single()
      if (sErr) throw sErr

      await logActivity({
        scope: 'students', action: 'consulting_claimed',
        summary: `Logged a free consulting session for ${student.name} (${minutes} min)`,
        entityTable: 'students', entityId: student.id, metadata: { studentId: student.id, sessionId: sess?.id }, actor,
      })
      onDone()
    } catch (e) {
      console.error('Claim failed:', e); setErr('Could not log the consulting session.')
    } finally { setBusy(false) }
  }

  return (
    <Modal title="Log free consulting session" subtitle={`${student.name} · no minutes will be deducted`} onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex items-center gap-2">
          <Gift className="h-4 w-4 flex-shrink-0" /> This uses one earned consulting credit and won't touch the student's balance.
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Start time</label>
          <input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">End time</label>
          <input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm" />
        </div>
        {err && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {err}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-60 flex items-center gap-2">
            {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {busy ? 'Logging…' : 'Log session'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
